import {
    Component,
    ElementRef,
    NgZone,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
    ChangeDetectorRef,
    model,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EventFormStore } from "../event.form.store";
import { AiStore } from "../../../modules/s25-chat-bot/s25.ai.store";
import { Item } from "../../../pojo/Item";
import { S25Const } from "../../../util/s25-const";
import { S25Bool } from "../../../util/core/s25.bool";
import { S25Util } from "../../../util/s25-util";
import { EventFormOccurrenceUtil } from "../../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { S25ObjectSearchModule } from "../../../modules/s25-object-search/s25.object.search.module";
import {
    ObjectSearchRefresh,
    S25ObjectSearchComponent,
} from "../../../modules/s25-object-search/s25.object.search.component";
import { ObjectSearchAvail } from "../../../modules/s25-object-search/object.search.avail";
import type { Layout } from "../../../modules/s25-object-search/s25.object.search.util";
import type { S25Event } from "../../../modules/s25-event/EventMicroI";
import type { S25Reservation } from "../../../modules/s25-event/ReservationI";
import { Event } from "../../../pojo/Event";
import type { AvailCheck } from "../../../pojo/AvailabilityCheck";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import type { EventForm } from "..";

function recommendedPrefSearchEqual(
    left: DropDownItem<string> | undefined,
    right: DropDownItem<string> | undefined,
): boolean {
    if (left === right) return true;
    if (!left || !right) return !left && !right;
    return left.txt === right.txt && left.val === right.val;
}

interface OccurrenceDate {
    startDt: Date;
    endDt: Date;
    occUUID?: number;
    eventId: number;
    profileId: number;
    segmentName?: string;
    isBoundSegment?: boolean;
}

export namespace EventFormObjSearch {
    export interface AngJSObject {
        itemTypeId: Item.Ids.Location | Item.Ids.Resource;
        /** Form-model id; seeded from spaceId or resourceId. */
        itemId: number;
        itemName?: string;
        spaceId?: number;
        spaceName?: string;
        share?: boolean;
        layouts?: Layout[];
        resourceId?: number;
        resourceName?: string;
        /** only used for resource; the amount requested by the user */
        defaultResourceQuantity?: number;
        /** only used for resource; represents total quantity */
        quantity?: number | string;
        allResAvailDateMap?: AvailCheck.ResAvailDateMap;
        instructions?: string;
        checkedObj: AvailCheck.CheckedObj;
    }

    export namespace AngJSObject {
        export function fromAddedItem(
            item: ObjectSearchAvail.AddedItem,
            itemTypeId: ObjectSearchAvail.ItemType,
        ): AngJSObject {
            const adapted: AngJSObject = {
                ...item,
                itemTypeId,
                itemId: "spaceId" in item ? item.spaceId : item.resourceId,
                itemName: "spaceId" in item ? item.spaceName : item.resourceName,
                checkedObj: item.checkedObj ?? ({ conflicts: [] } as unknown as AvailCheck.CheckedObj),
            };

            if (itemTypeId === Item.Ids.Resource && "resourceId" in item) {
                adapted.defaultResourceQuantity ??= item.quantity ?? 1;
                if (item.itemQuantity != null) {
                    adapted.quantity = item.itemQuantity;
                }
            }

            return adapted;
        }
    }
}

@TypeManagerDecorator("event-form-object-availability")
@Component({
    selector: "event-form-object-availability",
    host: {
        "[attr.data-type]": "itemType()",
    },
    template: `
        <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
            {{ liveAnnouncement() }}
        </div>
        @if (dirtyFlag()) {
            <div class="ngDirtySearch c-padding-bottom--double">
                <div>Availability for this search is out of date. Click Refresh to update the results.</div>
                <button
                    class="aw-button aw-button--outline event-form-availability__refresh"
                    [disabled]="refreshing()"
                    (click)="refresh()"
                >
                    {{ this.refreshing() ? "Refreshing Search..." : "Refresh" }}
                </button>
            </div>
        }
        <s25-ng-object-search
            [hidden]="dirtyFlag()"
            [itemTypeId]="itemTypeId()"
            [eventId]="eventId()"
            [profileId]="profileId()"
            [occ]="firstOcc()"
            [event]="event()"
            [selectedItems]="reservationIds()"
            [expectedCount]="expectedCount()"
            [dates]="dates()"
            [disableAi]="true"
            [isEventForm]="true"
            [hideInstructions]="true"
            [hideAutoLoad]="recommendedMode()"
            [hideSavedSearchDropdown]="recommendedMode()"
            [profilePrefSearch]="profilePrefSearch()"
            [recommendedPrefSearch]="recommendedPrefSearch()"
            [extraCriteria]="recommendedQuery()"
            [defaultHideConflicts]="checkHideConflicts()"
            [hideHideConflicts]="hideHideConflicts()"
            [checkHideRequestConflicts]="checkHideRequestConflicts()"
            [hideHideRequestConflicts]="hideHideRequestConflicts()"
            [checkEnforceHeadcount]="checkEnforceHeadcount()"
            [hideEnforceHeadcount]="hideEnforceHeadcount()"
            [excludeExpress]="excludeExpress()"
            [suppressedDatetimeFields]="suppressedDatetimeFields()"
            (beforeSearch)="markSearchActive(true)"
            (resultsReset)="markSearchActive(false)"
            (resultsLoaded)="onResultsLoaded($event)"
            (refreshF)="onSearchRefresh($event)"
        />
    `,
    providers: [AiStore],
    styles: `
        :host {
            display: block;
            padding-top: 0.75em;
        }
        :host ::ng-deep .s25ngTable .s25-ng-table--table > thead {
            position: sticky;
        }
        :host ::ng-deep .s25ngTable .tableWrapper {
            max-height: 500px;
            height: unset;
        }
        .event-form-availability__refresh {
            margin-top: 0.5rem;
        }
        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
    `,
    standalone: true,
    imports: [S25ObjectSearchModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.Emulated,
})
export class EventFormObjectAvailabilityComponent {
    readonly itemTypeId = input.required<EventForm.ObjectTypeId>();
    /**
     * The event-form model. AngularJS mutates this object in-place
     * and Angular's signal graph cannot observe in-place mutations, so we
     * never read from this directly in computeds — use `formModel`.
     * Direct reads are reserved for write-paths (mutating the form model)
     * and one-time setup that runs after inputs bind.
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    readonly angJSModel = input.required<EventForm>({ alias: "model" });
    readonly itemsAdded = output<void>();

    public readonly cd = inject(ChangeDetectorRef);
    private readonly zone = inject(NgZone);
    private readonly elementRef = inject(ElementRef<HTMLElement>);
    /** Optional only because specs render this component without a form scope. */
    private readonly eventForm = inject(EventFormStore, { optional: true });
    public readonly search = viewChild.required(S25ObjectSearchComponent);

    /**
     * Top-level spread of `angJSModel`, refreshed every 500ms. The spread
     * gives the signal a fresh top-level reference each tick so the signal
     * graph invalidates dependent computeds. Downstream computeds rely on
     * their own value equality to short-circuit when nothing actually changed.
     */
    readonly formModel = linkedSignal(() => ({ ...this.angJSModel() }));

    readonly eventId = computed(() => this.formModel().eventId);
    readonly eventName = computed(() => this.formModel().eventName);
    readonly profileId = computed(() => this.formModel().profileId);
    readonly locations = computed(() => (this.formModel().locations ?? []).map((loc) => ({ ...loc })), {
        equal: S25Util.array.isEqualDeep,
    });
    readonly resources = computed(() => (this.formModel().resources ?? []).map((res) => ({ ...res })), {
        equal: S25Util.array.isEqualDeep,
    });
    /** Preference / pending-approval resources not yet assigned on the form. */
    readonly prefRsrvList = computed(() => this.formModel().prefRsrvList ?? [], {
        equal: S25Util.array.isEqualDeep,
    });
    /** Saved associated resource preferences (query-backed prefs with request quantity). */
    readonly resourcePrefs = computed(() => this.formModel().resourcePrefs ?? [], {
        equal: S25Util.array.isEqualDeep,
    });
    /** a pruned view of the resObjOccs, for more efficient memoization */
    readonly resObjOccs = computed(
        () =>
            (this.formModel().getResOccs?.() ?? []).map((objOcc) => ({
                item: { itemId: objOcc.item.itemId },
                isIncluded: objOcc.isIncluded,
                resourceQuantity: objOcc.resourceQuantity,
            })),
        { equal: S25Util.array.isEqualDeep },
    );
    readonly itemType = computed(() => S25Const.itemId2Name[this.itemTypeId()]);
    readonly dirtyFlag = computed(() => !!this.formModel().dirtySearch?.[this.itemTypeId()]);
    readonly recommendedMode = computed(() => {
        const fm = this.formModel();
        return this.itemTypeId() === Item.Ids.Location ? !!fm.hasRecommendedLocation : !!fm.hasRecommendedResource;
    });
    readonly excludeExpress = computed(
        () => this.itemTypeId() === Item.Ids.Location && !!this.formModel().excludeExpressLocations,
    );
    readonly suppressedDatetimeFields = computed(() => this.formModel().suppressedDatetimeFields ?? []);
    readonly expectedCount = computed(() => {
        const raw = this.formModel().itemDict?.expected?.data;
        const parsed = parseInt(typeof raw === "string" ? raw : `${raw ?? ""}`, 10);
        return isNaN(parsed) ? 0 : parsed;
    });
    readonly dates = computed<OccurrenceDate[]>(
        () => {
            const currentOccurrences = (this.formModel().occurrences ?? []).filter((occ) => {
                const stateId = S25Util.parseInt(occ.state.itemId);
                return ![Event.State.Ids.Cancelled, Event.State.Ids.Confirmed].includes(stateId) && occ.includeInSearch;
            });
            const boundDates = (this.formModel().boundOccurrences ?? [])
                .filter(
                    (occurrence) =>
                        ![Event.State.Ids.Cancelled, Event.State.Ids.Confirmed].includes(occurrence.reservationState),
                )
                .map((occurrence) => ({
                    occUUID: occurrence.reservationId,
                    startDt: new Date(occurrence.startDt),
                    endDt: new Date(occurrence.endDt),
                    eventId: occurrence.eventId,
                    profileId: occurrence.profileId,
                    segmentName: occurrence.eventName,
                    isBoundSegment: true,
                }));
            // Segment labels only mean something once more than one segment is being checked
            const currentDates = currentOccurrences.map((occ) => ({
                occUUID: occ.uuid,
                startDt: EventFormOccurrenceUtil.getStartDt(occ),
                endDt: EventFormOccurrenceUtil.getEndDt(occ),
                eventId: this.eventId(),
                profileId: this.profileId(),
                segmentName: boundDates.length ? this.eventName() : undefined,
                isBoundSegment: false,
            }));

            const uniqueDates = [...currentDates, ...boundDates].filter(
                (dateRange, index, dates) =>
                    dates.findIndex(
                        (candidate) =>
                            candidate.startDt.getTime() === dateRange.startDt.getTime() &&
                            candidate.endDt.getTime() === dateRange.endDt.getTime(),
                    ) === index,
            );
            uniqueDates.sort(S25Util.shallowSortDates("startDt", "endDt"));
            return uniqueDates;
        },
        { equal: S25Util.array.isEqualDeep },
    );
    readonly firstOcc = computed<S25Reservation | undefined>(() => this.reservations()[0]);
    readonly reservations = computed<S25Reservation[]>(() => this.deriveReservations());
    readonly reservationIds = computed(() => this.reservations().map((reservation) => reservation.itemId));
    readonly event = computed<S25Event | undefined>(() => {
        const eventId = this.eventId();
        if (!eventId) return undefined;
        // Object search uses getProfileTimes(event) when checking layout setup/takedown conflicts.
        // Reservation dates already include occurrence additional time, so the synthetic event must
        // expose the same setup/takedown so setupDiff is not double-counted (always treated as 0).
        const minutes = this.formModel().timeModel.minutes;
        return {
            itemId: eventId,
            itemName: this.eventName() ?? "",
            profile: [
                {
                    itemId: this.profileId(),
                    reservations: this.reservations(),
                    occurrenceDefn: {
                        // Only addedTime is consumed by getProfileTimes; other fields are unused here.
                        addedTime: {
                            setup: S25Util.minutesToISODuration(minutes.setupMinutes || 0),
                            takedown: S25Util.minutesToISODuration(minutes.takedownMinutes || 0),
                        },
                    },
                },
            ],
        } as S25Event;
    });
    readonly checkHideConflicts = computed(() => !!this.formModel().checkHideConflicts);
    readonly hideHideConflicts = computed(() => !!this.formModel().hideHideConflicts);
    readonly checkHideRequestConflicts = computed(() => !!this.formModel().checkHideRequestConflicts);
    readonly hideHideRequestConflicts = computed(() => !!this.formModel().hideHideRequestConflicts);
    readonly checkEnforceHeadcount = computed(() => !!this.formModel().checkEnforceHeadcount);
    readonly hideEnforceHeadcount = computed(() => !!this.formModel().hideEnforceHeadcount);
    readonly recommendedQuery = computed(() => {
        if (!this.recommendedMode()) return undefined;
        return this.recommendedSearchVal();
    });
    readonly recommendedSearchVal = computed(() => {
        const list =
            this.itemTypeId() === Item.Ids.Location
                ? this.formModel().recommendedLocations
                : this.formModel().recommendedResources;
        return this.buildRecommendedSearchVal(this.itemTypeId(), list, this.recommendedMode());
    });
    /** Saved-search dropdown entry when Use Recommended is off but rules add items. */
    readonly recommendedPrefSearch = computed(
        (): DropDownItem<string> | undefined => {
            if (this.recommendedMode()) return undefined;
            const itemTypeId = this.itemTypeId();
            if (!this.recommendedSearchVal()) return undefined;
            return {
                txt: itemTypeId === Item.Ids.Location ? "Recommended Locations" : "Recommended Resources",
                val: this.recommendedSearchVal(),
                grp: "Pre-Defined Groups",
                type: "predef",
            };
        },
        { equal: recommendedPrefSearchEqual },
    );
    readonly profilePrefSearch = computed(() => {
        const model = this.formModel();
        return this.itemTypeId() === Item.Ids.Location ? model.spacePrefSearch : model.resourcePrefSearch;
    });

    readonly refreshing = signal(false);
    readonly searchActive = signal(false);
    readonly resultCount = signal<number | undefined>(undefined);

    readonly itemTypePlural = computed(() => (this.itemTypeId() === Item.Ids.Location ? "locations" : "resources"));

    readonly liveAnnouncement = computed(() => {
        if (this.searchActive()) {
            return `Searching ${this.itemTypePlural()}...`;
        }
        const count = this.resultCount();
        if (count === undefined) return "";
        if (count === 0) return `No ${this.itemTypePlural()} found`;
        return `Found ${count} ${count === 1 ? this.itemTypePlural().slice(0, -1) : this.itemTypePlural()}`;
    });

    constructor() {
        effect(() => this.installLegacyShim());

        // Spread the live form model into our snapshot when AngularJS mutates it
        // in place. Outside NgZone so we don't kick a global CD; signal-graph
        // propagation alone drives localized re-renders.
        // TODO: kill it with fire
        effect(() => {
            this.recommendedSearchVal();
            this.refreshSnapshot();
        });

        effect((onCleanup) => {
            const handle = this.zone.runOutsideAngular(() =>
                setInterval(() => {
                    if (!this.elementRef.nativeElement.isConnected) {
                        clearInterval(handle);
                        return;
                    }
                    this.refreshSnapshot();
                }, 500),
            );
            onCleanup(() => clearInterval(handle));
        });
    }

    async refresh() {
        if (this.refreshing()) return;
        try {
            this.refreshing.set(true);
            await this.search().refreshCurrentSearch();
        } catch (err) {
            console.error(err);
        } finally {
            this.refreshing.set(false);
            const model = this.angJSModel();
            if (model.dirtySearch) model.dirtySearch[this.itemTypeId()] = false;
            this.refreshSnapshot();
            this.eventForm?.bump();
        }
    }

    async onSearchRefresh(payload: ObjectSearchRefresh): Promise<void> {
        if (!payload?.items?.length) return;
        const model = this.angJSModel();
        // probably more aggressive than we need to be, but it's angularJS
        if (typeof model.addObject !== "function") return;
        const itemTypeId = this.itemTypeId();

        for (const item of payload.items) {
            const adapted = EventFormObjSearch.AngJSObject.fromAddedItem(item, itemTypeId);
            const result = await model.addObject(adapted, false, "autoConflictResolve", false, false);
            if (result && typeof result === "object" && "err" in result && result.err === "Cancelled") {
                this.search()?.searchResultsList()?.revertPendingAddButtons(payload.items);
                return;
            }
        }
        this.itemsAdded.emit();
        this.refreshSnapshot();
    }

    public refreshSnapshot(): void {
        this.formModel.set({ ...this.angJSModel() });
    }

    markSearchActive(active: boolean): void {
        const bean = this.angJSModel();
        bean.searchCounts ??= {};
        bean.searchCounts[this.itemTypeId()] = active ? 1 : 0;
        this.searchActive.set(active);
        if (active) {
            this.resultCount.set(undefined);
        }
        this.refreshSnapshot();
        this.eventForm?.bump();
    }

    onResultsLoaded(totalRows: number): void {
        this.searchActive.set(false);
        this.resultCount.set(totalRows);
    }

    private deriveReservations(): S25Reservation[] {
        const itemTypeId = this.itemTypeId();
        const reservedLocations =
            itemTypeId === Item.Ids.Location
                ? this.locations().map((loc) => ({
                      itemId: loc.itemId,
                      itemName: loc.itemName,
                      share: S25Bool.toBool(loc.always_shared),
                      always_shared: loc.always_shared,
                  }))
                : [];
        const reservedResources =
            itemTypeId === Item.Ids.Resource
                ? this.resources().map((res) => {
                      const quantity = this.getResourceQuantity(res.itemId, res.defaultResourceQuantity);
                      return {
                          itemId: res.itemId,
                          itemName: res.itemName,
                          ...(quantity === "mixed" ? { mixedOccQuantities: true } : { quantity }),
                      };
                  })
                : [];
        const draftResources =
            itemTypeId === Item.Ids.Resource ? this.deriveDraftResourcesFromPrefs(reservedResources) : [];

        return this.dates().map((dateRange, idx) => ({
            itemId: idx + 1,
            occUUID: dateRange.occUUID,
            state: 1,
            eventId: dateRange.eventId,
            profileId: dateRange.profileId,
            segmentName: dateRange.segmentName,
            isBoundSegment: dateRange.isBoundSegment,
            setupStart: dateRange.startDt,
            eventStart: dateRange.startDt,
            eventEnd: dateRange.endDt,
            takeDownEnd: dateRange.endDt,
            locations: reservedLocations.length ? [{ reserved: reservedLocations }] : [],
            resources:
                reservedResources.length || draftResources.length
                    ? [{ reserved: reservedResources, draft: draftResources }]
                    : [],
        })) as S25Reservation[];
    }

    /**
     * Preference / pending-approval resources from prefRsrvList and saved
     * associated resource_pref rows, carried as `draft` so Associated Resources
     * can seed quantity without treating them as Added (ANG-7556).
     * Skips resources already assigned on the form (those live in `reserved`).
     * When quantities differ, seeds with the first seen (pending prefs first).
     */
    private deriveDraftResourcesFromPrefs(
        reservedResources: { itemId: number }[],
    ): { itemId: number; itemName?: string; quantity?: number }[] {
        const reservedIds = new Set(reservedResources.map((resource) => resource.itemId));
        const firstQuantityByResourceId = new Map<number, number>();
        const nameByResourceId = new Map<number, string>();

        for (const prefRsrv of this.prefRsrvList()) {
            for (const rsRsrv of prefRsrv.resource_reservation ?? []) {
                this.seedDraftResource(
                    firstQuantityByResourceId,
                    nameByResourceId,
                    reservedIds,
                    rsRsrv.resource_id,
                    rsRsrv.quantity,
                    rsRsrv.resource_name,
                );
            }
        }

        for (const prefResource of this.resourcePrefs()) {
            this.seedDraftResource(
                firstQuantityByResourceId,
                nameByResourceId,
                reservedIds,
                prefResource.pref_resource_id,
                prefResource.pref_request_quantity,
                prefResource.pref_resource_name,
            );
        }

        return [...firstQuantityByResourceId.entries()].map(([resourceId, quantity]) => ({
            itemId: resourceId,
            itemName: nameByResourceId.get(resourceId),
            quantity,
        }));
    }

    private seedDraftResource(
        firstQuantityByResourceId: Map<number, number>,
        nameByResourceId: Map<number, string>,
        reservedIds: Set<number>,
        resourceIdRaw: number | string | undefined,
        quantityRaw: number | string | undefined,
        nameRaw: string | undefined,
    ): void {
        const resourceId = parseInt(`${resourceIdRaw}`, 10);
        if (!Number.isFinite(resourceId) || reservedIds.has(resourceId)) return;

        const parsedQuantity = parseInt(`${quantityRaw}`, 10);
        const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
        if (!firstQuantityByResourceId.has(resourceId)) {
            firstQuantityByResourceId.set(resourceId, quantity);
        }
        if (nameRaw && !nameByResourceId.has(resourceId)) {
            nameByResourceId.set(resourceId, `${nameRaw}`);
        }
    }

    private getResourceQuantity(resourceId: number, defaultResourceQuantity?: number): number | "mixed" {
        const quantities = this.resObjOccs()
            .filter((objOcc) => objOcc.item.itemId === resourceId && objOcc.isIncluded !== false)
            .map((objOcc) => S25Util.parseInt(objOcc.resourceQuantity ?? defaultResourceQuantity ?? 1));
        if (!quantities.length) {
            return defaultResourceQuantity ?? 1;
        }
        if (new Set(quantities).size > 1) {
            return "mixed";
        }
        return quantities[0];
    }

    private buildRecommendedSearchVal(
        itemTypeId: EventForm.ObjectTypeId,
        list: { itemId?: number }[] | undefined,
        emptyPlaceholder: boolean,
    ): string | undefined {
        const ids = (list ?? [])
            .map((entry) => entry?.itemId)
            .filter((id): id is number => id != null)
            .sort((first, second) => first - second)
            .join("+");
        if (!ids && !emptyPlaceholder) return undefined;
        const param = itemTypeId === Item.Ids.Location ? "space_id" : "resource_id";
        return `&${param}=${ids || "-9999"}`;
    }

    private installLegacyShim(): void {
        const bean = this.angJSModel();
        bean.actionModel ??= {};
        const itemTypeId = this.itemTypeId();
        bean.actionModel[itemTypeId] ??= {};
        bean.actionModel[itemTypeId].listModelBean ??= {
            showColumn: () => {},
            hideColumn: () => {},
            getRow: () => undefined,
            deferred: undefined,
            scope: undefined,
        };
        bean.actionModel[itemTypeId].listModelBean!.getRow = (itemId: number) =>
            this.search()?.searchResultsList()?.getRequestRowIndex(itemId);
        bean.actionModel[itemTypeId].listModelBean!.getCell = (rowIndex: number, colPrefname: string) =>
            this.search()?.searchResultsList()?.getRequestCellHandle(rowIndex, colPrefname);
    }
}
